import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api, apiUrl } from './api'
import { useApp } from './store'
import type { Member } from './types'

export function useH5Realtime() {
  const { member, setMember } = useApp()
  const client = useQueryClient()

  useEffect(() => {
    if (!member?.id) return

    let stopped = false
    let needsVisibleSync = false
    let lastVisibleSyncAt = 0
    let memberTimer = 0
    let orderTimer = 0
    let leaderboardTimer = 0
    let memberRefreshRunning = false

    const visible = () => document.visibilityState === 'visible'
    const deferWhenHidden = () => {
      if (visible()) return false
      needsVisibleSync = true
      return true
    }
    const refreshMember = async () => {
      if (stopped || memberRefreshRunning || deferWhenHidden()) return
      memberRefreshRunning = true
      try {
        const updated = await api<Member>('/api/h5/me')
        if (!stopped) setMember(updated)
      } catch {
        // EventSource reconnect and the visible-page sync provide the retry path.
      } finally {
        memberRefreshRunning = false
      }
    }
    const scheduleMemberRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(memberTimer)
      memberTimer = window.setTimeout(() => void refreshMember(), 100)
    }
    const scheduleOrderRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(orderTimer)
      orderTimer = window.setTimeout(() => {
        void client.invalidateQueries({ queryKey: ['orders'], refetchType: 'active' })
        void client.invalidateQueries({ queryKey: ['recharge-order'], refetchType: 'active' })
      }, 150)
    }
    const scheduleLeaderboardRefresh = () => {
      if (deferWhenHidden()) return
      window.clearTimeout(leaderboardTimer)
      leaderboardTimer = window.setTimeout(() => {
        void client.invalidateQueries({ queryKey: ['leaderboard'], refetchType: 'active' })
      }, 250 + Math.floor(Math.random() * 500))
    }
    const syncVisibleState = () => {
      if (!visible()) {
        needsVisibleSync = true
        return
      }
      const now = Date.now()
      if (!needsVisibleSync && now - lastVisibleSyncAt < 1000) return
      needsVisibleSync = false
      lastVisibleSyncAt = now
      scheduleMemberRefresh()
      void client.invalidateQueries({ queryKey: ['orders'], refetchType: 'active' })
      void client.invalidateQueries({ queryKey: ['recharge-order'], refetchType: 'active' })
      void client.invalidateQueries({ queryKey: ['point-ledgers'], refetchType: 'active' })
      void client.invalidateQueries({ queryKey: ['leaderboard'], refetchType: 'active' })
    }

    const source = new EventSource(apiUrl('/api/h5/events'), { withCredentials: true })
    source.addEventListener('ready', syncVisibleState)
    source.addEventListener('member-assets-changed', () => {
      scheduleMemberRefresh()
      if (!deferWhenHidden()) {
        void client.invalidateQueries({ queryKey: ['point-ledgers'], refetchType: 'active' })
        void client.invalidateQueries({ queryKey: ['lottery-status'], refetchType: 'active' })
        void client.invalidateQueries({ queryKey: ['h5-checkin-status'], refetchType: 'active' })
      }
    })
    source.addEventListener('order-changed', scheduleOrderRefresh)
    source.addEventListener('leaderboard-changed', scheduleLeaderboardRefresh)
    document.addEventListener('visibilitychange', syncVisibleState)
    window.addEventListener('pageshow', syncVisibleState)
    window.addEventListener('focus', syncVisibleState)

    return () => {
      stopped = true
      source.close()
      window.clearTimeout(memberTimer)
      window.clearTimeout(orderTimer)
      window.clearTimeout(leaderboardTimer)
      document.removeEventListener('visibilitychange', syncVisibleState)
      window.removeEventListener('pageshow', syncVisibleState)
      window.removeEventListener('focus', syncVisibleState)
    }
  }, [client, member?.id, setMember])
}
